import React from 'react';
import Datalist, { ListColumn } from '../Datalist/Datalist';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faPhone, faCheckCircle, faExclamationCircle, faTimesCircle, faMinusCircle,faCommentDots ,faEnvelope, faClipboardCheck , faChevronDown, faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons';
import './NullReportingTable.scss';
import { NullReportingSnapshot } from '../../data/API/Nullreporting';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import NullReportingCommentsModal from '../InternalComments/NullReportingCommentsModal';
import { NullReportingReviewSummaryRow, NullReportingReviewsAPI } from '../../data/API/NullReportingReview';
import { Dropdown } from 'react-bootstrap';

type Props = {
  items: NullReportingSnapshot[];
  isLoading: boolean;
  filter: string;     // green/yellow/red
  searchText: string;
  selectedDate:Date|null;
};
type Supplier = {
    orgNumber:string;
    period:string;
}
const money = (v: number) =>
  new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(v ?? 0);
const reviewStatusLabel = (s: number) => {
  switch (s) {
    case 0: return "Utkast";
    case 1: return "Skickad";
    case 2: return "Svar mottaget";
    case 3: return "Slutmarkerad";
    default: return "Okänd";
  }
};
const NullReportingTable: React.FC<Props> = ({ items, isLoading, filter, searchText,selectedDate }) => {
    const formattedSelectedDate = selectedDate ? format(selectedDate, 'yyyy-MM') : null;
    const navigate = useNavigate(); // Use the navigate hook for navigation
    const [showCommentsModal, setShowCommentsModal] = React.useState(false);
const [commentsTarget, setCommentsTarget] = React.useState<{ orgNr: string; period: string } | null>(null);
const [reviewSummary, setReviewSummary] = React.useState<Record<string, NullReportingReviewSummaryRow>>({});
//const [reviewCache, setReviewCache] = React.useState<Record<string, Array<{ id:number; status:number; receiverMail:string; created?:string }>>>({});
const [reviewCache, setReviewCache] = React.useState<
  Record<string, Array<{ id: number; status: number; reviewType: 0 | 1; receiverMail?: string | null; created?: string }>>
>({});

const [openKey, setOpenKey] = React.useState<string | null>(null);
const cacheKeyFor = (orgNr: string, period: string) => `${orgNr}|${period}`;
const ensureReviewsLoaded = async (orgNr: string, period: string) => {
  console.log('ENSURE REVIEW CACHE')
  const k = cacheKeyFor(orgNr, period);
  console.log(k)
  if (reviewCache[k]) return;

  const rows = await NullReportingReviewsAPI.listBySupplierAndPeriod(orgNr, period);
  setReviewCache(prev => ({ ...prev, [k]: rows }));
};
React.useEffect(() => {
  const period = formattedSelectedDate;
  if (!period) return;

  let cancelled = false;

  (async () => {
    try {
      const rows = await NullReportingReviewsAPI.summaryForPeriod(period);
      if (cancelled) return;

      const map: Record<string, NullReportingReviewSummaryRow> = {};
      for (const r of rows) map[r.supplierOrgNr] = r;
      setReviewSummary(map);
    } catch {
      if (!cancelled) setReviewSummary({});
    }
  })();

  return () => {
    cancelled = true;
  };
}, [formattedSelectedDate]);
const openComments = (orgNr: string, period: string) => {
  setCommentsTarget({ orgNr, period });
  setShowCommentsModal(true);
};
        // Function to handle row click
        const handleRowClick = (supplier: NullReportingSnapshot) => {
            // Navigate to the ReviewSupplierPage using orgNumber and period
            navigate(`/nullreporting/${supplier.supplierOrgId}`, {
                state: {
                    orgNumber: supplier.supplierOrgId, // Only send orgNumber
                    period: supplier.period ?? formattedSelectedDate // Send period (date) for fetching relevant data
                },
            });
        };
        
  const filteredData = items.filter((row) => {
    // Klassificering -> “färg”
    // Antag: 0 None/OK, 1 Yellow, 2 Red, 3 NoThreshold
    const cls = row.classification ?? 0;

    const hasThreshold = cls !== 3;
    let color = '#07A235';
    let icon = faCheckCircle;

    if (cls === 2) { color = '#DC143C'; icon = faTimesCircle; }
    else if (cls === 1) { color = '#F5A32D'; icon = faExclamationCircle; }
    else if (cls === 3) { color = '#6c757d'; icon = faMinusCircle; }

    const matchesFilter = filter ? (
      (filter === 'green' && color === '#07A235') ||
      (filter === 'red' && color === '#DC143C') ||
      (filter === 'yellow' && color === '#F5A32D')
    ) : true;

    // samma beteende som SupplierTable: om filter vald, exkludera utan threshold
    if (filter && !hasThreshold) return false;

    const s = searchText.toLowerCase();
    const matchesSearch =
      row.supplierOrgId.toLowerCase().includes(s) ||
      row.period?.includes(searchText) ||
      String(row.totalAmountPeriod ?? '').includes(searchText);

    return matchesFilter && matchesSearch;
  });

  const columns: ListColumn<NullReportingSnapshot>[] = [
    {
      key: 'supplierOrgId',
      fieldName: 'supplierOrgId',
      name: 'Leverantör (orgnr)',
      isActive: true,
      sorting: true,
      minWidth: 140,
      maxWidth: '25%',
      onRender: (i) => i.supplierOrgId,
      footer: (rows) => `Totalt antal rader: ${rows.length}`,
    },
    {
  key: "internalComments",
  fieldName: "internalComments",
  name: "",
  maxWidth: 50,
  minWidth: 50,
  isActive: true,
  sorting: false,
  onRender: (item: any) => (
    <span
      onClick={(ev) => {
        ev.stopPropagation();
        openComments(item.supplierOrgId, item.period);
      }}
      style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
      title="Interna kommentarer"
    >
      <FontAwesomeIcon
        icon={faCommentDots}
        style={{ color: item.hasInternalComment ? "#0d6efd" : "#adb5bd" }}
      />
    </span>
  ),
},
{
  key: "reviews",
  fieldName: "reviews",
  name: "",
  maxWidth: 80,
  minWidth: 80,
  isActive: true,
  sorting: false,
  onRender: (item: any) => {
    // Kräver att du har summary-data per supplier+period inbakad i snapshot items
    // ex: item.reviewTotalCount, item.reviewLatestStatus, item.reviewLatestId
    const total = item.reviewTotalCount ?? 0;
    if (total === 0) return '';

    const orgNr = item.supplierOrgId;
    const period = item.period;
    const k = cacheKeyFor(orgNr, period);

    // ikonfärg baserat på latestStatus
    const latestStatus = item.reviewLatestStatus ?? 0;
    const latestType = item.reviewLatestType ?? 0; 
    const color =
      latestStatus === 3 ? "#07A235" :
      latestStatus === 2 ? "#0d6efd" :
      latestStatus === 1 ? "#F5A32D" :
      "#6c757d"; // draft/unknown
     const icon = latestType === 1 ? faPhone : faEnvelopeOpenText;
    return (
      <div
        onClick={(ev) => ev.stopPropagation()}
        style={{ display: "flex", alignItems: "center", gap: 6 }}
      >
        <FontAwesomeIcon icon={icon} style={{ color }}  title={`Granskningar: ${total}`} />
        
        <Dropdown
          onToggle={async (isOpen) => {
            // Bootstrap togglar både öppna/stäng
            console.log('toggle',isOpen,k)
            if (isOpen) {
              setOpenKey(k);
              await ensureReviewsLoaded(orgNr, period);
            } else if (openKey === k) {
              setOpenKey(null);
            }
          }}
        >
          <Dropdown.Toggle variant="link" size="sm" style={{ padding: 0 }}  >
            {/* <FontAwesomeIcon icon={faChevronDown} /> */}
          </Dropdown.Toggle>

          <Dropdown.Menu align='end' popperConfig={{strategy:'fixed'}}>
            {(reviewCache[k] ?? []).map((r) => (
              <Dropdown.Item
                key={r.id}
                onClick={() => navigate(`/nullreporting/reviews/${r.id}`)}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <span style={{ fontWeight: 700 }}>#{r.id}</span>
                  <span>{reviewStatusLabel(r.status)}</span>
                </div>
                <div style={{ fontSize: 12, opacity: 0.8 }}>
  {r.reviewType === 1 ? "Telefon" : (r.receiverMail ?? "—")}
</div>
              </Dropdown.Item>
            ))}
            {(reviewCache[k]?.length ?? 0) === 0 && (
              <Dropdown.Item disabled>Inga granskningar</Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  },
},

    {
      key: 'period',
      fieldName: 'period',
      name: 'Period',
      isActive: true,
      sorting: true,
      minWidth: 90,
      maxWidth: '12%',
      onRender: (i) => i.period,
    },
    {
      key: 'totalAmountPeriod',
      fieldName: 'totalAmountPeriod',
      name: 'Summa (ex moms)',
      isActive: true,
      sorting: true,
      minWidth: 140,
      maxWidth: '18%',
      onRender: (i) => money(i.totalAmountPeriod),
    },
    {
      key: 'history',
      fieldName: 'history',
      name: 'Historik',
      isActive: true,
      sorting: true,
      minWidth: 120,
      maxWidth: '15%',
      onRender: (i) => `${i.zeroMonths}/${i.historyMonths} mån`,
    },
    {
      key: 'zeroRatio',
      fieldName: 'zeroRatio',
      name: 'Nollgrad',
      isActive: true,
      sorting: true,
      minWidth: 110,
      maxWidth: '12%',
      onRender: (i) => `${(i.zeroRatio ?? 0).toFixed(2)}%`,
    },
    {
      key: 'flag',
      fieldName: 'flag',
      name: 'Flagga',
      isActive: true,
      sorting: true,
      minWidth: 110,
      maxWidth: '18%',
      onRender: (i) => {
        const cls = i.classification ?? 0;

        let color = '#07A235';
        let icon = faCheckCircle;
        let label = 'OK';

        if (cls === 2) { color = '#DC143C'; icon = faTimesCircle; label = 'Röd'; }
        else if (cls === 1) { color = '#F5A32D'; icon = faExclamationCircle; label = 'Gul'; }
        else if (cls === 3) { color = '#6c757d'; icon = faMinusCircle; label = 'Ingen tröskel'; }

        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FontAwesomeIcon icon={icon} style={{ marginRight: 8, color }} />
            <span>{label}</span>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
      <Datalist
        items={filteredData}
        isLoading={isLoading}
        columns={columns}
        setColumns={() => {}}
        initialSortKey="flag"
        descendingSort={true}
        onClick={(item: NullReportingSnapshot) => handleRowClick(item)} 
        noItemsText="Inga nollrapporter att visa"
        uniqueKey="id"
      />
      {commentsTarget && (
  <NullReportingCommentsModal
    show={showCommentsModal}
    onClose={() => setShowCommentsModal(false)}
    supplierOrgNr={commentsTarget.orgNr}
    period={commentsTarget.period}
    onChanged={() => {
      // samma beteende som sendings: refetch list so icon updates
      // Här beror det på var din data kommer från:
      // - Om parent har fetchNullReporting(period) -> skicka in som prop.
      // - Annars: trigga en “refetch” callback
    }}
  />
)}
    </div>
  );
};

export default NullReportingTable;
